import React, {useCallback, useEffect, useRef, useState} from 'react';
import AuthImage from '../AuthImage';
import Spinner from '../Spinner';
import {AlertTriangleIcon, BoxIcon, CameraIcon, CheckCheckIcon, ScanIcon} from '../icons';
import {
    applyPackingCheck,
    fetchLatestPackingCheck,
    fetchPackingCheck,
    formatConfidence,
    isPackingCheckInProgress,
    startPackingCheck,
    TripPackingCheck,
    TripPackingCheckMatch,
} from '../../service/TripApi';

/** Интервал опроса статуса проверки: AI-вызов занимает секунды, не минуты. */
const POLL_INTERVAL_MS = 3000;

interface Props {
    tripId: string;
    /** Есть ли вообще что проверять: без плана сборов кнопка бессмысленна. */
    hasItems: boolean;
    /** Проверку применили — карточке поездки нужно перечитать список вещей. */
    onApplied: () => void;
    onError: (message: string) => void;
}

/**
 * Проверка сборов по фото: снимок разложенных вещей сверяется с планом упаковки.
 *
 * <p>Результат ничего не меняет сам: «нашлось» и «похоже на …» — это галочки, которые
 * человек подтверждает кнопкой. Модель ошибается, и автоматическая отметка «упаковано»
 * приводит к поездке без вещи — цена такой ошибки выше, чем лишний клик.</p>
 */
const TripPackingCheckSection: React.FC<Props> = ({tripId, hasItems, onApplied, onError}) => {
    const [check, setCheck] = useState<TripPackingCheck | null>(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
    const fileInput = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let cancelled = false;
        fetchLatestPackingCheck(tripId)
            .then(latest => {
                if (!cancelled) {
                    setCheck(latest);
                    setConfirmed(defaultConfirmed(latest));
                }
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [tripId]);

    const inProgress = isPackingCheckInProgress(check?.status);

    // Опрос идёт только пока проверка в работе: готовый результат перечитывать незачем.
    useEffect(() => {
        if (!check || !inProgress) {
            return undefined;
        }
        const timer = setInterval(() => {
            fetchPackingCheck(tripId, check.id)
                .then(fresh => {
                    setCheck(fresh);
                    if (!isPackingCheckInProgress(fresh.status)) {
                        setConfirmed(defaultConfirmed(fresh));
                    }
                })
                .catch(() => undefined);
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [tripId, check, inProgress]);

    const upload = useCallback(async (file: File) => {
        setUploading(true);
        try {
            const started = await startPackingCheck(tripId, file);
            setCheck(started);
            setConfirmed({});
        } catch (e) {
            onError(checkErrorText(e));
        } finally {
            setUploading(false);
        }
    }, [tripId, onError]);

    const apply = async () => {
        if (!check) {
            return;
        }
        const ids = Object.keys(confirmed).filter(id => confirmed[id]);
        setApplying(true);
        try {
            await applyPackingCheck(tripId, check.id, ids);
            onApplied();
        } catch (e) {
            onError(checkErrorText(e));
        } finally {
            setApplying(false);
        }
    };

    const toggle = (tripItemId: string) =>
        setConfirmed(prev => ({...prev, [tripItemId]: !prev[tripItemId]}));

    if (loading) {
        return (
            <div className="flex justify-center py-6" data-testid="trip-check-loading"><Spinner/></div>
        );
    }

    const confirmedCount = Object.values(confirmed).filter(Boolean).length;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <input
                    ref={fileInput}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="trip-check-file"
                    onChange={event => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (file) {
                            upload(file);
                        }
                    }}
                />
                <button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    disabled={!hasItems || uploading || inProgress}
                    data-testid="trip-check-start"
                    className="btn-primary min-h-[44px] inline-flex items-center gap-2"
                >
                    <CameraIcon size={18}/>
                    {uploading ? 'Загружаем…' : 'Проверить по фото'}
                </button>
                {!hasItems && (
                    <span className="text-sm text-gray-500 dark:text-gray-400" data-testid="trip-check-no-plan">
                        Сначала соберите план образов — проверять пока нечего.
                    </span>
                )}
            </div>

            {inProgress && (
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300"
                     data-testid="trip-check-progress" role="status">
                    <Spinner/>
                    Смотрим фото и сверяем со списком…
                </div>
            )}

            {check?.status === 'ERROR' && (
                <div role="alert" data-testid="trip-check-error"
                     className="p-3 rounded-lg border border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
                    {check.errorMessage ?? 'Не удалось проверить сборы. Попробуйте другое фото.'}
                </div>
            )}

            {!check && (
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="trip-check-empty">
                    Проверок ещё не было. Разложите вещи, сфотографируйте — система сверит снимок
                    со списком сборов.
                </p>
            )}

            {check?.status === 'READY' && (
                <div className="space-y-4" data-testid="trip-check-result">
                    <MatchGroup title="Нашлось" testId="trip-check-found" tone="text-green-700 dark:text-green-400"
                                icon={<CheckCheckIcon size={18}/>} items={check.found}
                                confirmed={confirmed} onToggle={toggle}/>
                    <MatchGroup title="Похоже на…" testId="trip-check-uncertain"
                                tone="text-amber-700 dark:text-amber-400" icon={<ScanIcon size={18}/>}
                                items={check.uncertain} confirmed={confirmed} onToggle={toggle}
                                hint="AI не уверен — проверьте вручную"/>
                    <MatchGroup title="Не хватает" testId="trip-check-missing"
                                tone="text-red-700 dark:text-red-400" icon={<AlertTriangleIcon size={18}/>}
                                items={check.missing} confirmed={confirmed} onToggle={toggle}/>

                    {check.extra.length > 0 && (
                        <section className="space-y-2" data-testid="trip-check-extra">
                            <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                                <BoxIcon size={18}/> Лишнее
                            </h3>
                            <ul className="space-y-1">
                                {check.extra.map(extra => (
                                    <li key={extra.label}
                                        className="text-sm text-gray-600 dark:text-gray-300">
                                        {extra.label}{extra.note ? ` — ${extra.note}` : ''}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <button
                        type="button"
                        onClick={apply}
                        disabled={applying || confirmedCount === 0}
                        data-testid="trip-check-apply"
                        className="min-h-[44px] px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                    >
                        {applying ? 'Сохраняем…' : `Отметить упакованными (${confirmedCount})`}
                    </button>
                </div>
            )}
        </div>
    );
};

interface GroupProps {
    title: string;
    testId: string;
    tone: string;
    icon: React.ReactNode;
    items: TripPackingCheckMatch[];
    confirmed: Record<string, boolean>;
    onToggle: (tripItemId: string) => void;
    hint?: string;
}

/** Группа результата: каждая строка — подтверждаемая галочка, а не свершившийся факт. */
const MatchGroup: React.FC<GroupProps> = ({title, testId, tone, icon, items, confirmed, onToggle, hint}) => {
    if (items.length === 0) {
        return null;
    }
    return (
        <section className="space-y-2" data-testid={testId}>
            <h3 className={`flex items-center gap-2 text-sm font-medium ${tone}`}>
                {icon} {title} · {items.length}
            </h3>
            {hint && <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
            <ul className="space-y-2">
                {items.map(item => {
                    const confidenceText = formatConfidence(item.confidence);
                    return (
                        <li key={item.tripItemId}
                            className="flex items-center gap-3 p-2 rounded-lg border border-gray-200 dark:border-gray-600">
                            <input
                                type="checkbox"
                                checked={Boolean(confirmed[item.tripItemId])}
                                onChange={() => onToggle(item.tripItemId)}
                                data-testid={`trip-check-confirm-${item.tripItemId}`}
                                aria-label={`Упаковано: ${item.name ?? 'вещь'}`}
                                className="w-5 h-5 min-w-[20px] accent-brand-500"
                            />
                            <span className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                                {item.primaryPhotoUrl ? (
                                    <AuthImage imageKey={item.primaryPhotoUrl} alt={item.name ?? ''}
                                               variant="thumbnail" className="w-full h-full object-cover"/>
                                ) : (
                                    <span className="w-full h-full flex items-center justify-center text-gray-400 text-xs">—</span>
                                )}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm text-gray-900 dark:text-gray-100 truncate">
                                    {item.name ?? 'Без названия'}
                                </span>
                                {(confidenceText || item.note) && (
                                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {[confidenceText, item.note].filter(Boolean).join(' · ')}
                                    </span>
                                )}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

/** По умолчанию отмечено только уверенно найденное: «похоже на …» подтверждает человек. */
export function defaultConfirmed(check: TripPackingCheck | null): Record<string, boolean> {
    const map: Record<string, boolean> = {};
    (check?.found ?? []).forEach(item => {
        map[item.tripItemId] = true;
    });
    return map;
}

/** Текст ошибки операции: описание backend, иначе общий fallback. */
export function checkErrorText(err: unknown): string {
    const data = (err as {response?: {data?: {error?: string; description?: string}}} | undefined)?.response?.data;
    return data?.description?.trim() || data?.error?.trim() || 'Не удалось проверить сборы.';
}

export default TripPackingCheckSection;
